import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clearRegisterState, fetchEventDetailsRequest, registerEventRequest } from '../store/eventSlice';
import EventAboutCard from '../components/eventDetails/EventAboutCard';
import EventDateCard from '../components/eventDetails/EventDateCard';
import EventHero from '../components/eventDetails/EventHero';
import EventImage from '../components/eventDetails/EventImage';
import EventMetaStrip from '../components/eventDetails/EventMetaStrip';
import EventTopBar from '../components/eventDetails/EventTopBar';
import EventVenueCard from '../components/eventDetails/EventVenueCard';
import MoreInformationCard from '../components/eventDetails/MoreInformationCard';
import PaymentMethods from '../components/eventDetails/PaymentMethods';
import MobileRegistrationBar from '../components/eventDetails/MobileRegistrationBar';
import NeedHelpCard from '../components/eventDetails/NeedHelpCard';
import RegistrationAvailability from '../components/eventDetails/RegistrationAvailability';
import RegistrationDialog from '../components/eventDetails/RegistrationDialog';
import { layoutStyles, topBarStyles } from '../components/eventDetails/eventDetailsStyles';
import { PAYMENT_SERVER_COMPLETE_PATH, PAYMENT_SERVER_CANCEL_PATH, getPaymentServerCallbackUrl } from '../routes/paymentRoutes';
import {
  fetchPaymentStatus,
  registerForFreeEvent,
  saveEventConsent,
  sendEventRegistrationOtp,
  validateEventCoupon,
  verifyEventRegistrationOtp,
} from '../services/api';
import {
  combineEventDateTime,
  formatCurrency,
  formatEventDateTime,
  getGoogleMapsUrl,
  getSessionTickets,
  isSessionAvailable,
  isTicketAvailable,
  withBaseUrl,
} from '../components/eventDetails/eventDetailsUtils';
import {
  COUNTRY_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  getFieldLengthValue,
  NAME_MAX_LENGTH,
  normalizePhoneNumber,
  PHONE_MAX_LENGTH,
  PHONE_REGEX,
} from '../components/eventDetails/registrationValidation';
import {
  Alert,
  Box,
  Button,
  Container,
  Skeleton,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ErrorState from '../components/ErrorState';

const getPaymentStatusAttendees = (paymentDetails) => {
  if (Array.isArray(paymentDetails?.attendees) && paymentDetails.attendees.length) return paymentDetails.attendees;
  if (paymentDetails?.attendee) return [paymentDetails.attendee];
  return [];
};

const resolveTicketCode = (tickets, rawTicketParam, ticketTypeId) => {
  const ticketParam = String(rawTicketParam || '').trim();
  const ticketByParam = tickets.find((ticket) => String(ticket.code) === ticketParam);
  if (ticketByParam) return ticketByParam.code;

  const ticketParamMatch = ticketParam.match(/^ticket-(\d+)$/i);
  const ticketIdFromParam = ticketParamMatch ? Number(ticketParamMatch[1]) : null;
  const effectiveTicketId = Number.isFinite(ticketIdFromParam) ? ticketIdFromParam : Number(ticketTypeId);
  if (!Number.isFinite(effectiveTicketId)) return '';

  const ticketById = tickets.find((ticket) => Number(ticket.ticket_type_id || ticket.id) === effectiveTicketId);
  return ticketById?.code || `ticket-${effectiveTicketId}`;
};

const getHostedCheckoutUrl = (purchaseResponse) => (
  purchaseResponse.checkout_url ||
  purchaseResponse.payment_url ||
  purchaseResponse.redirect_url ||
  purchaseResponse.hosted_checkout_url ||
  purchaseResponse.url
);

const getCheckoutProvider = (paymentType) => (
  paymentType === 'local_debit_apple_pay' ? 'qnb_all' : 'cybersource'
);

const ticketSalesStatusConfig = {
  open: { label: 'Registration open', unavailableMessage: '' },
  paused: { label: 'Registration paused', unavailableMessage: 'Registration is temporarily paused for this event.' },
  closed: { label: 'Registration closed', unavailableMessage: 'Registration is closed for this event.' },
  scheduled: { label: 'Registration not started', unavailableMessage: 'Registration has not started yet.' },
};

const SIDRA_EMAIL_DOMAIN = '@sidra.org';
const SIDRA_EMAIL_ID_REGEX = /^[A-Za-z0-9._%+-]+$/;

const getSidraEmailIdFromEmail = (email) => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail.endsWith(SIDRA_EMAIL_DOMAIN)) return '';
  return normalizedEmail.slice(0, -SIDRA_EMAIL_DOMAIN.length);
};

const getFirstErrorText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = getFirstErrorText(item);
      if (resolved) return resolved;
    }
    return '';
  }
  if (typeof value === 'object') {
    for (const nestedValue of Object.values(value)) {
      const resolved = getFirstErrorText(nestedValue);
      if (resolved) return resolved;
    }
  }
  return '';
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();

  if (data && typeof data === 'object') {
    const priorityKeys = ['otp', 'detail', 'message', 'non_field_errors', 'email', 'session_id', 'ticket_type_id'];
    for (const key of priorityKeys) {
      const resolved = getFirstErrorText(data[key]);
      if (resolved) return resolved;
    }

    for (const value of Object.values(data)) {
      const resolved = getFirstErrorText(value);
      if (resolved) return resolved;
    }
  }

  return error?.message || fallbackMessage;
};

const getFreeRegistrationReference = (responseData) => {
  if (!responseData || typeof responseData !== 'object') return '';

  const directReference = responseData.purchase_ref || responseData.reference || responseData.transaction_reference;
  if (directReference) return String(directReference);

  if (responseData.attendee?.purchase_ref) return String(responseData.attendee.purchase_ref);
  if (Array.isArray(responseData.attendees) && responseData.attendees[0]?.purchase_ref) {
    return String(responseData.attendees[0].purchase_ref);
  }

  return '';
};

const appendCheckoutField = (form, name, value) => {
  if (!name || value === undefined || value === null) return;
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = typeof value === 'object' ? JSON.stringify(value) : String(value);
  form.appendChild(input);
};

const submitHostedCheckoutForm = (checkoutUrl, checkoutFields) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = checkoutUrl;
  form.style.display = 'none';

  const fieldEntries = Array.isArray(checkoutFields)
    ? checkoutFields.map((field) => [field.name || field.field_name || field.key, field.value])
    : Object.entries(checkoutFields || {});

  fieldEntries.forEach(([name, value]) => {
    if (Array.isArray(value)) {
      value.forEach((fieldValue) => appendCheckoutField(form, name, fieldValue));
      return;
    }
    appendCheckoutField(form, name, value);
  });

  document.body.appendChild(form);
  form.submit();
};

const EventDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { eventDetails, loading, error, registering, registerError, purchaseResponse } = useSelector((state) => state.events);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [selectedTicketCode, setSelectedTicketCode] = useState('');
  const [openRegistrationModal, setOpenRegistrationModal] = useState(false);
  const [proceedingToPayment, setProceedingToPayment] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({
    fullName: '',
    email: '',
    sidraEmailId: '',
    sidraOtp: '',
    sidraVerificationToken: '',
    sidraOtpVerifiedEmail: '',
    sidraMarketingConsent: true,
    phone: '',
    country: '',
    quantity: 1,
    notes: '',
    studentFileName: '',
    paymentType: 'local_debit_apple_pay',
    additionalFields: {},
  });
  const [formErrors, setFormErrors] = useState({});
  const [registrationStep, setRegistrationStep] = useState(0);
  const [retryPaymentDetails, setRetryPaymentDetails] = useState(null);
  const [retryPrefillError, setRetryPrefillError] = useState('');
  const [retryPrefillLoading, setRetryPrefillLoading] = useState(false);
  const [retryPrefillApplied, setRetryPrefillApplied] = useState(false);
  const [sidraOtpSending, setSidraOtpSending] = useState(false);
  const [sidraOtpVerifying, setSidraOtpVerifying] = useState(false);
  const [sidraOtpStatus, setSidraOtpStatus] = useState(null);
  const [sidraOtpSent, setSidraOtpSent] = useState(false);
  const [sidraOtpCooldownUntil, setSidraOtpCooldownUntil] = useState(0);
  const [sidraOtpCooldownRemaining, setSidraOtpCooldownRemaining] = useState(0);
  const [freeRegisterError, setFreeRegisterError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);

  useEffect(() => {
    if (eventSlug) dispatch(fetchEventDetailsRequest(eventSlug));
  }, [dispatch, eventSlug]);

  useEffect(() => {
    setSelectedSessionId(searchParams.get('session') || '');
    setSelectedTicketCode(searchParams.get('ticket') || '');
  }, [eventSlug, searchParams]);

  const retryRef = (searchParams.get('ref') || '').trim();

  useEffect(() => {
    setRetryPrefillApplied(false);
  }, [eventSlug, retryRef]);

  useEffect(() => {
    if (!retryRef) {
      setRetryPaymentDetails(null);
      setRetryPrefillError('');
      setRetryPrefillLoading(false);
      return;
    }

    let ignore = false;
    setRetryPrefillLoading(true);
    setRetryPrefillError('');

    fetchPaymentStatus(retryRef)
      .then((response) => {
        if (ignore) return;
        console.log('Fetched previous attendee details:', response.data);
        setRetryPaymentDetails(response.data || null);
      })
      .catch((error) => {
        if (ignore) return;
        const message = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unable to load previous attendee details.';
        setRetryPrefillError(message);
        setRetryPaymentDetails(null);
      })
      .finally(() => {
        if (!ignore) setRetryPrefillLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [retryRef]);

  useEffect(() => {
    if (!purchaseResponse) return;
    const checkoutUrl = getHostedCheckoutUrl(purchaseResponse);
    const checkoutFields = purchaseResponse.checkout_fields || purchaseResponse.hosted_checkout_fields || purchaseResponse.fields;
    if (checkoutUrl) {
      if (checkoutFields && (Array.isArray(checkoutFields) ? checkoutFields.length : Object.keys(checkoutFields).length)) {
        submitHostedCheckoutForm(checkoutUrl, checkoutFields);
        return;
      }
      window.location.assign(checkoutUrl);
      return;
    }
    // Fully discounted registrations complete server-side without a checkout redirect.
    const purchaseRef = getFreeRegistrationReference(purchaseResponse);
    if (purchaseRef) {
      const nextParams = new URLSearchParams();
      nextParams.set('free', '1');
      nextParams.set('purchase_ref', purchaseRef);
      navigate(`/events/payment_complete/?${nextParams.toString()}`);
      dispatch(clearRegisterState());
    }
    setOpenRegistrationModal(false);
    setRegistrationStep(0);
  }, [dispatch, navigate, purchaseResponse]);

  useEffect(() => {
    if (!registering) {
      setProceedingToPayment(false);
    }
  }, [registering]);

  useEffect(() => {
    document.title = eventDetails?.title || 'Sidra Medicine';
  }, [eventDetails?.title]);

  const sessions = useMemo(() => eventDetails?.sessions || [], [eventDetails?.sessions]);
  const allTickets = useMemo(() => {
    const ticketsBySession = sessions.flatMap((session) => session.ticket_types || []);
    return ticketsBySession.length ? ticketsBySession : eventDetails?.ticket_types || [];
  }, [eventDetails?.ticket_types, sessions]);
  const selectedSession = useMemo(
    () => sessions.find((session) => String(session.id) === String(selectedSessionId)),
    [selectedSessionId, sessions],
  );
  const querySessionId = searchParams.get('session') || '';
  const queryTicketCode = searchParams.get('ticket') || '';
  const displayedSession = useMemo(() => {
    if (selectedSession) return selectedSession;
    const queryMatchedSession = sessions.find((session) => String(session.id) === String(querySessionId));
    return queryMatchedSession || sessions[0] || null;
  }, [querySessionId, selectedSession, sessions]);
  const availableTickets = useMemo(() => {
    if (!selectedSessionId) return [];
    return getSessionTickets(selectedSession, eventDetails?.ticket_types || []).filter(isTicketAvailable);
  }, [eventDetails?.ticket_types, selectedSession, selectedSessionId]);

  const selectedTicket = availableTickets.find((ticket) => ticket.code === selectedTicketCode);
  const isSidraEmployeeOnly = Boolean(selectedTicket?.for_sidra_employees_only);
  const sidraEmailPreview = registrationForm.sidraEmailId ? `${registrationForm.sidraEmailId.trim().toLowerCase()}${SIDRA_EMAIL_DOMAIN}` : SIDRA_EMAIL_DOMAIN;
  const quantity = Math.max(1, Number(registrationForm.quantity || 1));
  const totalPrice = Number(selectedTicket?.price || 0) * quantity;
  const couponDiscount = appliedCoupon ? Math.min(totalPrice, Number(appliedCoupon.discount_amount || 0)) : 0;
  const payableTotal = appliedCoupon ? Math.max(0, Number(appliedCoupon.total_amount ?? totalPrice - couponDiscount)) : totalPrice;
  const isStudentTicket = (selectedTicketCode || '').toLowerCase().includes('student');
  const shouldShowRemainingTickets = Boolean(eventDetails?.should_show_remaining_tickets);
  const selectedTicketSeatsLeft = shouldShowRemainingTickets && selectedTicket?.session_inventory_configured
    ? Number(selectedTicket.session_seats_left ?? 0)
    : null;
  const ticketSalesStatus = eventDetails?.ticket_sales_status || 'open';
  const availabilityStatus = eventDetails?.availability_status || ticketSalesStatus;
  const ticketSalesConfig = ticketSalesStatusConfig[availabilityStatus] || ticketSalesStatusConfig.open;
  const isTicketSalesOpen = availabilityStatus === 'open';
  const isSessionRegistrationOpen = (session) => (
    (session?.availability_status || availabilityStatus) === 'open'
  );
  const hasSessionInventory = sessions.some((session) => (
    isSessionRegistrationOpen(session) && isSessionAvailable(session, eventDetails?.ticket_types || [])
  ));
  const hasAvailableSessions = isTicketSalesOpen && hasSessionInventory;
  const scheduledSession = sessions.find((session) => session.availability_status === 'scheduled');
  const availabilityReason = eventDetails?.availability_reason
    || scheduledSession?.availability_reason
    || ticketSalesConfig.unavailableMessage;
  const availableFromValue = eventDetails?.available_from || scheduledSession?.available_from;
  const availableFrom = availableFromValue
    ? ` Registration opens ${formatEventDateTime(availableFromValue)}.`
    : '';
  const registrationUnavailableMessage = isTicketSalesOpen
    ? 'This event is fully booked. Registration is currently unavailable.'
    : `${availabilityReason}${availableFrom}`;
  const fromPrice = allTickets.length ? Math.min(...allTickets.map((ticket) => Number(ticket.price || 0))) : null;
  const displayedVenue = displayedSession?.venue || null;
  const displayedLocationLabel = displayedSession?.location_label || displayedSession?.location || eventDetails?.location_label;
  const contactDetails = {
    ...(eventDetails?.contact_details || {}),
    email: eventDetails?.contact_details?.email || displayedVenue?.email || 'events@sidra.org',
    phone: eventDetails?.contact_details?.phone || displayedVenue?.phone || '',
  };
  const supportingDocuments = useMemo(() => {
    const rawDocuments = Array.isArray(eventDetails?.supporting_documents) ? eventDetails.supporting_documents : [];
    return rawDocuments
      .filter((document) => document?.should_show_in_event_details)
      .map((document) => {
        const href = document.url || (document.attachment_url ? withBaseUrl(document.attachment_url) : '');
        return {
          ...document,
          href,
          isAttachment: Boolean(!document.url && document.attachment_url),
        };
      })
      .filter((document) => Boolean(document.href));
  }, [eventDetails?.supporting_documents]);
  const additionalRegistrationFields = useMemo(() => {
    const configuredFields = selectedTicket?.registration?.additional_registration_fields || [];

    return [...configuredFields]
      .sort((firstField, secondField) => Number(firstField.display_order || 0) - Number(secondField.display_order || 0));
  }, [selectedTicket?.registration?.additional_registration_fields]);

  const updateSelectionParams = (sessionId, ticketCode = '') => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (sessionId) {
        nextParams.set('session', sessionId);
      } else {
        nextParams.delete('session');
      }

      if (ticketCode) {
        nextParams.set('ticket', ticketCode);
      } else {
        nextParams.delete('ticket');
      }

      return nextParams;
    }, { replace: true });
  };

  useEffect(() => {
    if (!eventDetails || selectedSessionId || !isTicketSalesOpen) return;
    const querySession = sessions.find((session) => String(session.id) === querySessionId
      && isSessionRegistrationOpen(session)
      && isSessionAvailable(session, eventDetails.ticket_types || []));
    const firstAvailableSession = querySession || sessions.find((session) => (
      isSessionRegistrationOpen(session) && isSessionAvailable(session, eventDetails.ticket_types || [])
    ));
    if (firstAvailableSession) {
      const nextSessionId = String(firstAvailableSession.id);
      const sessionTickets = getSessionTickets(firstAvailableSession, eventDetails.ticket_types || []).filter(isTicketAvailable);
      const nextTicketCode = sessionTickets.some((ticket) => ticket.code === queryTicketCode) ? queryTicketCode : '';
      setSelectedSessionId(nextSessionId);
      setSelectedTicketCode(nextTicketCode);
      updateSelectionParams(nextSessionId, nextTicketCode);
    }
    // updateSelectionParams intentionally stays outside deps to avoid rewriting params on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDetails, isTicketSalesOpen, querySessionId, queryTicketCode, selectedSessionId, sessions]);

  useEffect(() => {
    if (!selectedSessionId) return;

    const soleAvailableTicket = availableTickets.length === 1 ? availableTickets[0] : null;
    if (soleAvailableTicket && selectedTicketCode !== soleAvailableTicket.code) {
      setSelectedTicketCode(soleAvailableTicket.code);
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.set('ticket', soleAvailableTicket.code);
        return nextParams;
      }, { replace: true });
      return;
    }

    if (selectedTicketCode || !queryTicketCode) return;
    const matchingTicket = availableTickets.find((ticket) => ticket.code === queryTicketCode);
    if (matchingTicket) {
      setSelectedTicketCode(queryTicketCode);
    }
  }, [availableTickets, queryTicketCode, selectedSessionId, selectedTicketCode, setSearchParams]);

  useEffect(() => {
    if (!isSidraEmployeeOnly) {
      setSidraOtpStatus(null);
      setSidraOtpSent(false);
      setRegistrationForm((prev) => {
        if (!prev.sidraEmailId && !prev.sidraOtp && !prev.sidraVerificationToken && !prev.sidraOtpVerifiedEmail) return prev;
        return {
          ...prev,
          sidraEmailId: '',
          sidraOtp: '',
          sidraVerificationToken: '',
          sidraOtpVerifiedEmail: '',
        };
      });
      return;
    }

    const normalizedSidraEmailId = String(registrationForm.sidraEmailId || '').trim().toLowerCase();
    const derivedEmail = normalizedSidraEmailId ? `${normalizedSidraEmailId}${SIDRA_EMAIL_DOMAIN}` : '';
    setRegistrationForm((prev) => (prev.email === derivedEmail ? prev : { ...prev, email: derivedEmail }));
  }, [isSidraEmployeeOnly, registrationForm.sidraEmailId]);

  useEffect(() => {
    if (!retryPaymentDetails || !eventDetails || retryPrefillLoading || retryPrefillApplied) return;

    const attendees = getPaymentStatusAttendees(retryPaymentDetails);
    const primaryAttendee = attendees[0];
    if (!primaryAttendee) {
      setRetryPrefillApplied(true);
      return;
    }

    const nextSessionId = String(primaryAttendee.session_id || searchParams.get('session') || '');
    const sessionMatch = sessions.find((session) => String(session.id) === nextSessionId);
    const sessionTickets = getSessionTickets(sessionMatch, eventDetails?.ticket_types || []);
    const requestedTicket = searchParams.get('ticket') || '';
    const nextTicketCode = resolveTicketCode(sessionTickets, requestedTicket, primaryAttendee.ticket_type_id);

    if (nextSessionId) setSelectedSessionId(nextSessionId);
    if (nextTicketCode) setSelectedTicketCode(nextTicketCode);

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (nextSessionId) nextParams.set('session', nextSessionId);
      if (nextTicketCode) nextParams.set('ticket', nextTicketCode);
      nextParams.set('ref', primaryAttendee.purchase_ref || retryRef);
      nextParams.set('retry', '1');
      return nextParams;
    }, { replace: true });

    setRegistrationForm((previousForm) => {
      const prefilledEmail = primaryAttendee.email || previousForm.email;
      return {
        ...previousForm,
        fullName: primaryAttendee.name || previousForm.fullName,
        email: prefilledEmail,
        sidraEmailId: getSidraEmailIdFromEmail(prefilledEmail),
        sidraOtp: '',
        sidraVerificationToken: '',
        sidraOtpVerifiedEmail: '',
        phone: primaryAttendee.phone || previousForm.phone,
        country: primaryAttendee.country || previousForm.country,
        quantity: Math.max(1, Number(primaryAttendee.quantity || previousForm.quantity || 1)),
        additionalFields: {
          ...previousForm.additionalFields,
          ...(primaryAttendee.registration_data && typeof primaryAttendee.registration_data === 'object'
            ? primaryAttendee.registration_data
            : {}),
        },
      };
    });

    dispatch(clearRegisterState());
    setProceedingToPayment(false);
    setFormErrors({});
    setSidraOtpStatus(null);
    setSidraOtpSent(false);
    setSidraOtpCooldownUntil(0);
    setSidraOtpCooldownRemaining(0);
    setRegistrationStep(0);
    setOpenRegistrationModal(true);
    setRetryPrefillApplied(true);
  }, [
    dispatch,
    eventDetails,
    retryPaymentDetails,
    retryPrefillApplied,
    retryPrefillLoading,
    retryRef,
    searchParams,
    sessions,
    setSearchParams,
  ]);

  const handleSessionSelect = (session) => {
    if (!isTicketSalesOpen) return;
    if (!isSessionRegistrationOpen(session)) return;
    if (!isSessionAvailable(session, eventDetails?.ticket_types || [])) return;
    const nextSessionId = String(session.id);
    setSelectedSessionId(nextSessionId);
    setSelectedTicketCode('');
    updateSelectionParams(nextSessionId);
  };

  const handleTicketSelect = (ticket) => {
    if (!isTicketAvailable(ticket)) return;
    const nextTicketCode = ticket.code;
    setSelectedTicketCode(nextTicketCode);
    setRegistrationForm((prev) => ({ ...prev, quantity: getNextQuantity(prev.quantity, ticket) }));
    setFormErrors((prev) => ({ ...prev, quantity: '' }));
    updateSelectionParams(selectedSessionId, nextTicketCode);
  };

  const handleOpenRegistration = () => {
    if (!isTicketSalesOpen || !selectedSession || !selectedTicketCode) return;
    dispatch(clearRegisterState());
    setProceedingToPayment(false);
    setFreeRegisterError('');
    setFormErrors({});
    setSidraOtpStatus(null);
    setSidraOtpSent(false);
    setSidraOtpCooldownUntil(0);
    setSidraOtpCooldownRemaining(0);
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
    setRegistrationStep(0);
    setOpenRegistrationModal(true);
  };

  const handleCloseRegistration = () => {
    dispatch(clearRegisterState());
    setProceedingToPayment(false);
    setFreeRegisterError('');
    setSidraOtpStatus(null);
    setSidraOtpSent(false);
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
    setOpenRegistrationModal(false);
    setRegistrationStep(0);
  };

  const handleFormChange = (field) => (event) => {
    const rawValue = event.target.value;
    const value = field === 'sidraEmailId'
      ? String(rawValue || '').replace(/\s+/g, '').toLowerCase()
      : rawValue;

    setRegistrationForm((prev) => {
      const nextForm = { ...prev, [field]: value };
      if (field === 'sidraEmailId') {
        nextForm.sidraOtp = '';
        nextForm.sidraVerificationToken = '';
        nextForm.sidraOtpVerifiedEmail = '';
      }
      return nextForm;
    });

    setFormErrors((prev) => ({
      ...prev,
      [field]: '',
      ...(field === 'sidraEmailId' ? { sidraOtp: '' } : {}),
    }));

    if (field === 'sidraEmailId') {
      setSidraOtpStatus(null);
      setSidraOtpSent(false);
      setSidraOtpCooldownUntil(0);
      setSidraOtpCooldownRemaining(0);
    }
  };

  const handleMarketingConsentChange = (value) => {
    setRegistrationForm((prev) => ({ ...prev, sidraMarketingConsent: Boolean(value) }));
  };

  const saveMarketingConsentInBackground = () => {
    const attendeeEmail = isSidraEmployeeOnly ? buildSidraEmail() : registrationForm.email.trim();
    if (!attendeeEmail) return;

    const consentText = 'I would like to receive marketing communications about future events. By registering for this event and/or if you choose to opt-in to receive marketing/event related communications for future events, you agree to our terms and acknowledge our Privacy Notice.';

    void saveEventConsent(eventSlug, {
      email: attendeeEmail,
      consent_marketing: Boolean(registrationForm.sidraMarketingConsent),
      consent_disclaimer: true,
      consent_text: consentText,
    }).catch(() => {
      // Intentionally ignore consent API failures so registration continues.
    });
  };

  const handleAdditionalFieldChange = (fieldName) => (event) => {
    setRegistrationForm((prev) => ({
      ...prev,
      additionalFields: {
        ...prev.additionalFields,
        [fieldName]: event.target.value,
      },
    }));
    setFormErrors((prev) => ({ ...prev, [`additionalFields.${fieldName}`]: '' }));
  };

  const getTicketQuantityLimit = (ticket) => {
    const limits = [];
    const maxPurchaseLimit = Number(ticket?.max_purchase_limit);

    if (ticket?.session_inventory_configured) {
      limits.push(Number(ticket.session_seats_left ?? 0));
    }

    if (Number.isFinite(maxPurchaseLimit) && maxPurchaseLimit > 0) {
      limits.push(maxPurchaseLimit);
    }

    const validLimits = limits.filter((limit) => Number.isFinite(limit));
    return validLimits.length ? Math.min(...validLimits) : null;
  };

  const getNextQuantity = (value, ticket = selectedTicket) => {
    const nextQuantity = Math.max(1, Number(value || 1));
    const ticketLimit = getTicketQuantityLimit(ticket);
    return ticketLimit === null ? nextQuantity : Math.min(nextQuantity, ticketLimit);
  };

  const handleQuantityChange = (ticket) => (nextQuantity) => {
    setRegistrationForm((prev) => ({ ...prev, quantity: getNextQuantity(nextQuantity, ticket) }));
    setFormErrors((prev) => ({ ...prev, quantity: '' }));
  };

  const handleStudentFileChange = (event) => {
    const fileName = event.target.files?.[0]?.name || '';
    setRegistrationForm((prev) => ({ ...prev, studentFileName: fileName }));
    setFormErrors((prev) => ({ ...prev, studentFileName: '' }));
  };

  const resetCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  useEffect(() => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
  }, [selectedTicketCode, selectedSessionId, quantity]);

  const handleCouponCodeChange = (event) => {
    setCouponCode(event.target.value.toUpperCase());
    resetCoupon();
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    resetCoupon();
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponError('Enter a coupon code.');
      return;
    }
    if (!selectedTicket) return;

    setCouponApplying(true);
    setCouponError('');

    try {
      const response = await validateEventCoupon(eventSlug, {
        code,
        ticket_type_id: Number(selectedTicket.ticket_type_id || selectedTicket.id),
        quantity,
        email: isSidraEmployeeOnly ? buildSidraEmail() : registrationForm.email.trim(),
      });

      if (!response?.data?.valid) {
        setAppliedCoupon(null);
        setCouponError(getFirstErrorText(response?.data?.detail) || 'Invalid coupon code.');
        return;
      }

      setAppliedCoupon(response.data);
    } catch (error) {
      setAppliedCoupon(null);
      setCouponError(getApiErrorMessage(error, 'Unable to apply this coupon.'));
    } finally {
      setCouponApplying(false);
    }
  };

  const normalizeSidraEmailInput = (value) => {
    const rawValue = String(value || '').trim().toLowerCase().replace(/\s+/g, '');
    if (!rawValue) return '';

    const [localPart = '', domainPart = ''] = rawValue.split('@');
    const normalizedLocalPart = String(localPart || '').trim();
    const normalizedDomain = String(domainPart || '').trim().toLowerCase();

    if (!normalizedLocalPart) return '';
    if (!normalizedDomain || normalizedDomain === 'sidra.org') {
      return `${normalizedLocalPart}${SIDRA_EMAIL_DOMAIN}`;
    }

    return `${normalizedLocalPart}${SIDRA_EMAIL_DOMAIN}`;
  };

  const buildSidraEmail = () => normalizeSidraEmailInput(registrationForm.sidraEmailId);

  useEffect(() => {
    if (!sidraOtpCooldownUntil) {
      setSidraOtpCooldownRemaining(0);
      return undefined;
    }

    const tick = () => {
      const remainingMs = Math.max(0, sidraOtpCooldownUntil - Date.now());
      const remainingSeconds = Math.ceil(remainingMs / 1000);
      setSidraOtpCooldownRemaining(remainingSeconds);

      if (remainingMs <= 0) {
        setSidraOtpCooldownUntil(0);
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 250);
    return () => window.clearInterval(intervalId);
  }, [sidraOtpCooldownUntil]);

  const handleSendSidraOtp = async () => {
    if (sidraOtpCooldownRemaining > 0) return;

    const sidraEmail = buildSidraEmail();
    if (!sidraEmail) {
      setFormErrors((prev) => ({ ...prev, sidraEmailId: 'Sidra email ID is required' }));
      return;
    }

    const sidraEmailId = sidraEmail.replace(SIDRA_EMAIL_DOMAIN, '');
    if (!SIDRA_EMAIL_ID_REGEX.test(sidraEmailId)) {
      setFormErrors((prev) => ({ ...prev, sidraEmailId: 'Enter a valid Sidra employee email or ID.' }));
      return;
    }
    if (!selectedSession?.id || !selectedTicket) return;

    setSidraOtpSending(true);
    setSidraOtpStatus(null);
    setFormErrors((prev) => ({ ...prev, sidraEmailId: '', sidraOtp: '' }));

    try {
      const email = sidraEmail;
      const response = await sendEventRegistrationOtp(eventSlug, {
        session_id: Number(selectedSession.id),
        ticket_type_id: Number(selectedTicket.ticket_type_id || selectedTicket.id),
        email,
      });

      const expiresIn = Number(response?.data?.expires_in);
      const expiresMinutes = Math.round(expiresIn / 60);

      const expiresSuffix =
        Number.isFinite(expiresMinutes) && expiresMinutes > 0
          ? ` Expires in ${expiresMinutes} minute${expiresMinutes !== 1 ? 's' : ''}.`
          : '';
      setRegistrationForm((prev) => ({
        ...prev,
        sidraOtp: '',
        sidraVerificationToken: '',
        sidraOtpVerifiedEmail: '',
      }));
      setSidraOtpSent(true);
      setSidraOtpCooldownUntil(Date.now() + 60000);
      setSidraOtpStatus({
        severity: 'info',
        message: `${response?.data?.message || 'OTP sent successfully.'}${expiresSuffix}`,
      });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Unable to send OTP.');
      setSidraOtpStatus({ severity: 'error', message });
    } finally {
      setSidraOtpSending(false);
    }
  };

  const handleVerifySidraOtp = async () => {
    const otp = String(registrationForm.sidraOtp || '').trim();
    if (!otp) {
      setFormErrors((prev) => ({ ...prev, sidraOtp: 'OTP is required' }));
      return;
    }
    if (otp.length < 4 || otp.length > 6) {
      setFormErrors((prev) => ({ ...prev, sidraOtp: 'OTP must be 4 to 6 characters' }));
      return;
    }
    if (!selectedSession?.id || !selectedTicket) {
      setSidraOtpStatus({ severity: 'error', message: 'Please select a valid session and ticket before OTP verification.' });
      return;
    }

    setSidraOtpVerifying(true);
    setSidraOtpStatus(null);
    setFormErrors((prev) => ({ ...prev, sidraOtp: '' }));

    try {
      const response = await verifyEventRegistrationOtp(eventSlug, {
        session_id: Number(selectedSession.id),
        ticket_type_id: Number(selectedTicket.ticket_type_id || selectedTicket.id),
        email: buildSidraEmail(),
        otp,
      });
      const verificationToken = response?.data?.verification_token || '';
      const verifiedEmail = String(response?.data?.email || buildSidraEmail()).trim().toLowerCase();

      if (!verificationToken) {
        throw new Error('Verification token missing in response');
      }

      setRegistrationForm((prev) => ({
        ...prev,
        sidraVerificationToken: verificationToken,
        sidraOtpVerifiedEmail: verifiedEmail,
      }));
      setSidraOtpStatus({ severity: 'success', message: response?.data?.message || 'OTP verified successfully.' });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Unable to verify OTP.');
      setSidraOtpStatus({ severity: 'error', message });
    } finally {
      setSidraOtpVerifying(false);
    }
  };

  const handleRegistrationSubmit = (event) => {
    event.preventDefault();

    const normalizedPhone = normalizePhoneNumber(registrationForm.phone);
    const attendeeEmail = isSidraEmployeeOnly ? buildSidraEmail() : registrationForm.email.trim();

    if (registrationStep === 1) {
      saveMarketingConsentInBackground();
      setProceedingToPayment(true);
      setFreeRegisterError('');
      const additionalRegistrationValues = additionalRegistrationFields.reduce((values, field) => {
        values[field.field_name] = (registrationForm.additionalFields[field.field_name] || '').trim();
        return values;
      }, {});

      const baseRegistrationPayload = {
        session_id: Number(selectedSession.id),
        ticket_type_id: Number(selectedTicket.ticket_type_id || selectedTicket.id),
        name: registrationForm.fullName.trim(),
        email: attendeeEmail,
        phone: normalizedPhone,
        country: registrationForm.country.trim(),
        quantity,
        notes: registrationForm.notes.trim(),
        registration_data: additionalRegistrationValues,
        ...(appliedCoupon?.code ? { coupon_code: appliedCoupon.code } : {}),
        ...(isSidraEmployeeOnly && registrationForm.sidraVerificationToken
          ? { sidra_verification_token: registrationForm.sidraVerificationToken }
          : {}),
      };

      if (totalPrice <= 0) {
        registerForFreeEvent(eventSlug, baseRegistrationPayload)
          .then((response) => {
            const purchaseRef = getFreeRegistrationReference(response?.data);
            const nextParams = new URLSearchParams();
            nextParams.set('free', '1');
            if (purchaseRef) nextParams.set('purchase_ref', purchaseRef);
            navigate(`/events/payment_complete/${nextParams.toString() ? `?${nextParams.toString()}` : ''}`);
            dispatch(clearRegisterState());
            setOpenRegistrationModal(false);
            setRegistrationStep(0);
          })
          .catch((error) => {
            setProceedingToPayment(false);
            setFreeRegisterError(getApiErrorMessage(error, 'Unable to complete free registration.'));
          });
        return;
      }

      dispatch(registerEventRequest({
        eventId: eventDetails?.id || eventSlug,
        payload: {
          ...baseRegistrationPayload,
          discount_amount: appliedCoupon ? String(appliedCoupon.discount_amount ?? couponDiscount) : '0.00',
          payment_method: 'online',
          payment_type: registrationForm.paymentType,
          checkout_provider: getCheckoutProvider(registrationForm.paymentType),
          return_url: getPaymentServerCallbackUrl(PAYMENT_SERVER_COMPLETE_PATH),
          cancel_url: getPaymentServerCallbackUrl(PAYMENT_SERVER_CANCEL_PATH),
        },
      }));
      return;
    }

    const nextErrors = {};
    const fullName = registrationForm.fullName.trim();
    const email = attendeeEmail;
    const country = registrationForm.country.trim();
    

    if (!fullName) {
      nextErrors.fullName = 'Full name is required';
    } else if (fullName.split(/\s+/).length < 2) {
      nextErrors.fullName = 'Please enter your last name';
    } else if (fullName.length > NAME_MAX_LENGTH) {
      nextErrors.fullName = `Full name must be ${NAME_MAX_LENGTH} characters or fewer`;
    }
    if (isSidraEmployeeOnly) {
      const sidraEmailInput = String(registrationForm.sidraEmailId || '').trim().toLowerCase();
      const normalizedSidraEmailId = sidraEmailInput.includes('@')
        ? sidraEmailInput.split('@')[0]
        : sidraEmailInput;
      const sidraOtpMissingOrUnverified = !registrationForm.sidraVerificationToken
        || !registrationForm.sidraOtpVerifiedEmail
        || registrationForm.sidraOtpVerifiedEmail !== email;

      if (!sidraEmailInput) {
        nextErrors.sidraEmailId = 'Sidra email ID is required';
      } else if (!SIDRA_EMAIL_ID_REGEX.test(normalizedSidraEmailId)) {
        nextErrors.sidraEmailId = 'Use only letters, numbers, and ._%+- characters';
      }
      if (email.length > EMAIL_MAX_LENGTH) {
        nextErrors.sidraEmailId = `Email address must be ${EMAIL_MAX_LENGTH} characters or fewer`;
      }
      if (sidraOtpMissingOrUnverified) {
        nextErrors.sidraOtp = 'Please verify your Sidra email with OTP before continuing';
      }
    } else if (!email) {
      nextErrors.email = 'Email address is required';
    } else if (email.length > EMAIL_MAX_LENGTH) {
      nextErrors.email = `Email address must be ${EMAIL_MAX_LENGTH} characters or fewer`;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = 'Enter a valid email address';
    }
    if (registrationForm.phone.length > PHONE_MAX_LENGTH) {
      nextErrors.phone = `Phone number must be ${PHONE_MAX_LENGTH} characters or fewer`;
    }
    if (!normalizedPhone) {
      nextErrors.phone = 'Phone number is required';
    } else if (!PHONE_REGEX.test(normalizedPhone)) {
      nextErrors.phone = 'Enter a valid phone number (8-15 digits, optional leading +).';
    }
    if (!country) {
      nextErrors.country = 'Country is required';
    } else if (country.length > COUNTRY_MAX_LENGTH) {
      nextErrors.country = `Country must be ${COUNTRY_MAX_LENGTH} characters or fewer`;
    }
    if (!quantity || quantity < 1) nextErrors.quantity = 'Quantity must be at least 1';
    if (!selectedTicket?.id && !selectedTicket?.ticket_type_id) nextErrors.ticket = 'Selected ticket is missing an ID';
    if (selectedTicket?.session_inventory_configured && quantity > Number(selectedTicket.session_seats_left ?? 0)) {
      nextErrors.quantity = shouldShowRemainingTickets
        ? `Only ${selectedTicket.session_seats_left} seats left for this ticket`
        : 'Selected quantity is no longer available';
    }
    const ticketQuantityLimit = getTicketQuantityLimit(selectedTicket);
    if (ticketQuantityLimit !== null && quantity > ticketQuantityLimit) {
      const maxPurchaseLimit = Number(selectedTicket?.max_purchase_limit);
      const isPurchaseLimit = Number.isFinite(maxPurchaseLimit) && maxPurchaseLimit > 0 && ticketQuantityLimit === maxPurchaseLimit;
      if (isPurchaseLimit) {
        nextErrors.quantity = `Maximum ${ticketQuantityLimit} ticket${ticketQuantityLimit === 1 ? '' : 's'} per transaction`;
      } else if (shouldShowRemainingTickets) {
        nextErrors.quantity = `Only ${ticketQuantityLimit} seats left for this ticket`;
      } else {
        nextErrors.quantity = 'Selected quantity is no longer available';
      }
    }
    if (isStudentTicket && !registrationForm.studentFileName) nextErrors.studentFileName = 'Student ID document is required';
    additionalRegistrationFields.forEach((field) => {
      const fieldValue = (registrationForm.additionalFields[field.field_name] || '').trim();
      const minLength = getFieldLengthValue(field.min_length);
      const maxLength = getFieldLengthValue(field.max_length);

      if (field.is_required && !fieldValue) {
        nextErrors[`additionalFields.${field.field_name}`] = `${field.label} is required`;
        return;
      }

      if (!fieldValue) return;

      if (minLength !== null && fieldValue.length < minLength) {
        nextErrors[`additionalFields.${field.field_name}`] = `${field.label} must be at least ${minLength} characters`;
        return;
      }

      if (maxLength !== null && fieldValue.length > maxLength) {
        nextErrors[`additionalFields.${field.field_name}`] = `${field.label} must be at most ${maxLength} characters`;
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }
    setRegistrationForm((prev) => (prev.email === email ? prev : { ...prev, email }));
    setRegistrationStep(1);
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={220} />
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Skeleton variant="rounded" height={340} sx={{ mb: 3 }} />
              <Skeleton variant="rounded" height={100} sx={{ mb: 3 }} />
              <Skeleton variant="rounded" height={180} sx={{ mb: 3 }} />
              <Skeleton variant="rounded" height={220} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Skeleton variant="rounded" height={300} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box sx={topBarStyles.shell}>
          <EventTopBar onBack={() => navigate('/')} />
        </Box>
        <ErrorState
          error={error}
          title="Unable to load event"
          onRetry={() => dispatch(fetchEventDetailsRequest(eventSlug))}
        />
      </Box>
    );
  }

  if (!eventDetails) return null;

  const heroImageSrc = withBaseUrl(eventDetails.hero_image || eventDetails.image_url);
  const paymentSubmitting = registering || proceedingToPayment;
  const dateTimeDisplayOptions = {
    displayDate: eventDetails.display_date,
    displayTime: eventDetails.display_time,
  };
  const eventStartValue = combineEventDateTime(
    eventDetails.event_start_date,
    eventDetails.event_start_time,
    eventDetails.sale_start || eventDetails.event_start || eventDetails.date_label,
  );
  const eventEndValue = combineEventDateTime(
    eventDetails.event_end_date,
    eventDetails.event_end_time,
    eventDetails.sale_end || eventDetails.event_end,
  );
  const eventStartDisplay = formatEventDateTime(
    eventStartValue,
    dateTimeDisplayOptions,
  );
  const eventEndDisplay = formatEventDateTime(
    eventEndValue,
    dateTimeDisplayOptions,
  );
  const mapsUrl = getGoogleMapsUrl(displayedVenue, displayedLocationLabel);
  const registrationStatus = hasAvailableSessions
    ? ticketSalesConfig.label
    : isTicketSalesOpen
      ? 'Fully booked'
      : ticketSalesConfig.label;
  const availabilityLabel = hasAvailableSessions ? 'Slots available' : isTicketSalesOpen ? 'Sold out' : ticketSalesConfig.label;

  return (
    <Box>
      <Box sx={topBarStyles.shell}>
        <EventTopBar onBack={() => navigate('/')} />
      </Box>

      <EventHero eventDetails={eventDetails} hasAvailableSessions={hasAvailableSessions} registrationStatus={registrationStatus} />

      <EventMetaStrip
        start={eventStartDisplay}
        venue={displayedVenue?.name || displayedLocationLabel || '-'}
        price={fromPrice === null ? '-' : `From ${formatCurrency(fromPrice)}`}
        availability={availabilityLabel}
      />

      <Container maxWidth="lg" sx={layoutStyles.pageShell}>
        {retryPrefillError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {`Could not preload attendee details for retry: ${retryPrefillError}`}
          </Alert>
        )}
        <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <EventImage src={heroImageSrc} title={eventDetails.title} />
            <EventAboutCard
              category={eventDetails.category}
              paragraphs={eventDetails.description_paragraphs || []}
              disclaimers={eventDetails.disclaimers || []}
              learningPoints={eventDetails.learning_points || []}
            />
            {(dateTimeDisplayOptions.displayDate || dateTimeDisplayOptions.displayTime) && (
              <EventDateCard category={eventDetails.category} start={eventStartDisplay} end={eventEndDisplay} />
            )}
            <RegistrationAvailability
              sessions={sessions}
              eventTickets={eventDetails.ticket_types || []}
              hasAvailableSessions={hasAvailableSessions}
              isTicketSalesOpen={isTicketSalesOpen}
              isSessionRegistrationOpen={isSessionRegistrationOpen}
              shouldShowRemainingTickets={shouldShowRemainingTickets}
              registrationUnavailableMessage={registrationUnavailableMessage}
              selectedSessionId={selectedSessionId}
              selectedTicketCode={selectedTicketCode}
              selectedSession={selectedSession}
              selectedTicket={selectedTicket}
              selectedTicketSeatsLeft={selectedTicketSeatsLeft}
              quantity={quantity}
              totalPrice={totalPrice}
              formErrors={formErrors}
              onSessionSelect={handleSessionSelect}
              onTicketSelect={handleTicketSelect}
              onQuantityChange={handleQuantityChange}
              getTicketQuantityLimit={getTicketQuantityLimit}
              onOpenRegistration={handleOpenRegistration}
            />
            <Box sx={{ mt: 3 }}>
              <EventVenueCard venue={displayedVenue} locationLabel={displayedLocationLabel} mapsUrl={mapsUrl} />
            </Box>
            {supportingDocuments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <MoreInformationCard documents={supportingDocuments} eventType={eventDetails.category} />
              </Box>
            )}
            <Box sx={{ mt: 3 }}>
              <PaymentMethods />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={layoutStyles.stickyAside}>
              <NeedHelpCard contactDetails={contactDetails} />
            </Box>
          </Grid>
        </Grid>
      </Container>

      <MobileRegistrationBar
        selectedSession={selectedSession}
        selectedTicket={selectedTicket}
        totalPrice={totalPrice}
        disabled={!selectedSession || !selectedTicketCode || !hasAvailableSessions}
        onConfirm={handleOpenRegistration}
      />

      <RegistrationDialog
        open={openRegistrationModal}
        onClose={handleCloseRegistration}
        registrationStep={registrationStep}
        onBackToDetails={() => setRegistrationStep(0)}
        selectedSession={selectedSession}
        selectedTicket={selectedTicket}
        selectedTicketSeatsLeft={selectedTicketSeatsLeft}
        quantity={quantity}
        totalPrice={totalPrice}
        payableTotal={payableTotal}
        couponDiscount={couponDiscount}
        isFreeTicket={payableTotal <= 0}
        couponCode={couponCode}
        appliedCoupon={appliedCoupon}
        couponError={couponError}
        couponApplying={couponApplying}
        showCouponSection={totalPrice > 0}
        onCouponCodeChange={handleCouponCodeChange}
        onApplyCoupon={handleApplyCoupon}
        onRemoveCoupon={handleRemoveCoupon}
        registerError={registerError || freeRegisterError}
        formErrors={formErrors}
        registrationForm={registrationForm}
        onFormChange={handleFormChange}
        onPaymentTypeChange={handleFormChange('paymentType')}
        onMarketingConsentChange={handleMarketingConsentChange}
        additionalRegistrationFields={additionalRegistrationFields}
        onAdditionalFieldChange={handleAdditionalFieldChange}
        isSidraEmployeeOnly={isSidraEmployeeOnly}
        sidraEmailDomain={SIDRA_EMAIL_DOMAIN}
        sidraEmailPreview={sidraEmailPreview}
        sidraOtpStatus={sidraOtpStatus}
        sidraOtpSent={sidraOtpSent}
        sidraOtpSending={sidraOtpSending}
        sidraOtpVerifying={sidraOtpVerifying}
        sidraOtpCooldownRemaining={sidraOtpCooldownRemaining}
        onSendSidraOtp={handleSendSidraOtp}
        onVerifySidraOtp={handleVerifySidraOtp}
        isStudentTicket={isStudentTicket}
        onStudentFileChange={handleStudentFileChange}
        registering={paymentSubmitting}
        onSubmit={handleRegistrationSubmit}
      />
    </Box>
  );
};

export default EventDetails;
